'use client';

import ReactECharts from 'echarts-for-react';
import {getTargetSkillset} from 'iq-onboarding/api';
import type {TopicWeight} from 'iq-onboarding/types';
import {type RadarAxis, selectAxes, wrapLabel} from 'iq-onboarding/utils';
import {useEffect, useState} from 'react';

type SkillsetRadarProps = {
  current: TopicWeight[];
  initialTargetSkillset: TopicWeight[];
  positionSlugs: string[];
  companySlugs: string[];
};

const CURRENT_COLOR = '#60A5FA'; // blue
const TARGET_COLOR = '#F59E0B'; // iq-orange

// Radar axis max as a multiple of the even share (1/n), so the shape reflects how peaked
// the distribution is rather than being normalized to its own max. A perfectly even
// skillset sits at ~1/SPREAD of the radius (a balanced mid-size polygon); a topic carrying
// SPREAD× the even share reaches the rim.
const SPREAD = 1.5;

// SkillsetRadar overlays the user's current skillset against the target skillset implied
// by their selected positions/companies. It owns the target: seeded from
// initialTargetSkillset, then refetched as the selection changes.
export function SkillsetRadar({
  current,
  initialTargetSkillset,
  positionSlugs,
  companySlugs,
}: SkillsetRadarProps) {
  const [target, setTarget] = useState<TopicWeight[]>(initialTargetSkillset);
  const [loading, setLoading] = useState(false);
  const selectionKey = `${positionSlugs.join(',')}|${companySlugs.join(',')}`;

  useEffect(() => setTarget(initialTargetSkillset), [initialTargetSkillset]);

  // `cancelled` drops a stale response if the selection changes again before this resolves.
  useEffect(() => {
    setLoading(true);
    let cancelled = false;
    (async () => {
      const skillset = await getTargetSkillset(positionSlugs, companySlugs);
      if (cancelled) return;
      setTarget(skillset);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // positionSlugs/companySlugs are captured via selectionKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  const hasCurrent = current.length > 0;
  const topics = selectAxes(current, target);
  const hasChart = topics.length > 0;

  return (
    <div className='flex flex-col gap-1'>
      <div className='text-center'>
        <div className='flex flex-col justify-end lg:h-[4.5rem]'>
          <h2 className='text-lg font-semibold text-white'>Target skillset</h2>
        </div>
        <p className='text-base text-white/50 mt-2'>
          {hasCurrent
            ? 'See how your current skills stack up against your target positions and companies.'
            : 'Skills based on the positions and companies you selected.'}
        </p>
      </div>
      {hasChart && renderChart(topics, hasCurrent, loading)}
    </div>
  );
}

// renderChart draws the radar. Callers must only invoke it with a non-empty topic set —
// echarts' radar coord errors on an empty indicator set.
function renderChart(topics: RadarAxis[], hasCurrent: boolean, loading: boolean) {
  const axisMax = (1 / topics.length) * SPREAD;
  const clamp = (w: number) => Math.min(Math.max(w, 0), axisMax);

  const option = {
    color: [TARGET_COLOR, CURRENT_COLOR],
    // Always render the legend (Current left, Target right) so its row reserves space — this
    // also hosts the recalculating indicator, avoiding the layout shift a separate loader caused.
    legend: {
      data: hasCurrent ? ['Current', 'Target'] : ['Target'],
      bottom: 0,
      textStyle: {color: '#ffffff', fontFamily: 'Overpass, sans-serif'},
      formatter: (name: string) =>
        name === 'Target' && loading ? 'Target (recalculating…)' : name,
    },
    radar: {
      shape: 'polygon',
      // Small negative min keeps zero-weight topics off the dead center.
      indicator: topics.map((t) => ({
        name: wrapLabel(t.topic),
        max: axisMax,
        min: -axisMax * 0.08,
      })),
      radius: '58%',
      axisName: {
        color: '#cbd5e1',
        fontFamily: 'Overpass, sans-serif',
        fontSize: 11,
      },
      splitLine: {lineStyle: {color: 'rgba(255,255,255,0.12)'}},
      splitArea: {areaStyle: {color: ['rgba(255,255,255,0.03)', 'rgba(255,255,255,0.06)']}},
      axisLine: {lineStyle: {color: 'rgba(255,255,255,0.12)'}},
    },
    series: [
      {
        type: 'radar',
        // Display-only chart — silence all mouse interaction.
        silent: true,
        data: [
          {
            name: 'Target',
            value: topics.map((t) => clamp(t.target)),
            areaStyle: {opacity: 0.25},
            lineStyle: {width: 2},
          },
          ...(hasCurrent
            ? [
                {
                  name: 'Current',
                  value: topics.map((t) => clamp(t.current)),
                  areaStyle: {opacity: 0.15},
                  lineStyle: {width: 2},
                },
              ]
            : []),
        ],
      },
    ],
    animation: true,
    animationDuration: 600,
    animationEasing: 'cubicInOut',
  };

  return (
    <ReactECharts
      option={option}
      notMerge={false}
      style={{height: 420, width: '100%'}}
      opts={{renderer: 'canvas'}}
    />
  );
}
